/**
 * Free-text "search by brand" box shown next to the Sort by control on
 * collection pages and the search results page.
 *
 * The customer types any brand name (there is no fixed list). Matching is
 * done against the product TITLE (this store's products don't use the
 * Shopify "vendor" field for the brand, the brand is part of the title,
 * e.g. "Maison Margiela Sneakers"), across EVERY product in the collection
 * (or every product the current search matched) - not just the page
 * currently rendered on screen - and the grid updates in place with no
 * full page reload.
 *
 * Data sources (both public, no theme backend needed):
 *  - Collections: /collections/<handle>/products.json (Shopify's built-in
 *    storefront JSON endpoint), paginated 250 at a time.
 *  - Search: /search?view=brand-search&q=... which renders
 *    templates/search.brand-search.liquid, paginated 250 at a time the
 *    same way.
 *
 * Results are fetched once per collection/search scope and cached in
 * memory, so every keystroke after the first filters locally instead of
 * re-fetching.
 */
(function () {
  'use strict';

  var MAX_RENDERED_RESULTS = 200;
  var DEBOUNCE_MS = 350;

  // Shopify's native /search endpoint does not support sorting results by
  // product creation date (only relevance/price are real sort_by values for
  // search - confirmed empirically: passing sort_by=created-descending is
  // silently ignored by Shopify and the results stay relevance-ordered).
  // To make "Date, new to old" a real, working default/option on the search
  // results page we fetch the FULL result set ourselves (the same endpoint
  // brand text search already uses) and sort/render it client-side instead.
  var DATE_SORT_VALUE = 'created-descending';

  var scopeCache = {};

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function getStrings() {
    // noResults / resultsCount intentionally always use this hardcoded
    // (English) template rather than a Liquid-translated one: both contain
    // a literal "{{ variable }}" placeholder that formatString() below
    // substitutes at runtime, and embedding literal "{{ }}" inside a Liquid
    // {{ ... | t }} output tag is not valid Liquid syntax. loading/soldOut
    // have no placeholders, so those two are safe to localize and come
    // from window.brandSearchStrings (set in snippets/facets.liquid).
    var defaults = {
      loading: 'Searching…',
      noResults: 'No products found for "{{ terms }}".',
      resultsCount: 'Showing {{ count }} matching products',
      dateSortedStatus: 'Showing {{ count }} products, newest first',
      soldOut: 'Sold out',
    };
    var provided = window.brandSearchStrings || {};
    return {
      loading: provided.loading || defaults.loading,
      noResults: defaults.noResults,
      resultsCount: defaults.resultsCount,
      dateSortedStatus: defaults.dateSortedStatus,
      soldOut: provided.soldOut || defaults.soldOut,
    };
  }

  function formatString(template, replacements) {
    return template.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match;
    });
  }

  function scopeKeyFor(container) {
    var scope = container.dataset.brandSearchScope;
    if (scope === 'collection') {
      return 'collection:' + container.dataset.brandSearchCollection;
    }
    return 'search:' + (container.dataset.brandSearchTerms || '');
  }

  function fetchAllProducts(container) {
    var key = scopeKeyFor(container);
    if (scopeCache[key]) return scopeCache[key];

    var scope = container.dataset.brandSearchScope;
    var promise;

    if (scope === 'collection') {
      var handle = container.dataset.brandSearchCollection;
      promise = fetchAllPages(function (page) {
        return (
          '/collections/' +
          encodeURIComponent(handle) +
          '/products.json?limit=250&page=' +
          page +
          '&fields=id,title,handle,available,images,variants,created_at'
        );
      }, parseCollectionPage);
    } else {
      var terms = container.dataset.brandSearchTerms || '';
      promise = fetchAllPages(function (page) {
        return (
          '/search?view=brand-search&type=product&options%5Bprefix%5D=last&q=' +
          encodeURIComponent(terms) +
          '&page=' +
          page
        );
      }, parseSearchPage);
    }

    scopeCache[key] = promise;
    return promise;
  }

  function fetchAllPages(urlForPage, parsePage) {
    var allProducts = [];

    function step(page) {
      return fetch(urlForPage(page), { headers: { accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) return { products: [], hasNextPage: false };
          return response.json().then(parsePage);
        })
        .then(function (parsed) {
          allProducts = allProducts.concat(parsed.products);
          if (parsed.hasNextPage && parsed.products.length > 0 && page < 40) {
            return step(page + 1);
          }
          return allProducts;
        })
        .catch(function () {
          return allProducts;
        });
    }

    return step(1);
  }

  function parseCollectionPage(json) {
    var products = (json.products || []).map(function (product) {
      var variant = (product.variants && product.variants[0]) || {};
      var image = (product.images && product.images[0]) || null;
      return {
        title: product.title,
        handle: product.handle,
        url: '/products/' + product.handle,
        available: !!product.variants && product.variants.some(function (v) {
          return v.available;
        }),
        price: variant.price != null ? parseFloat(variant.price) : null,
        compareAtPrice: variant.compare_at_price != null ? parseFloat(variant.compare_at_price) : null,
        image: image ? image.src : null,
        imageAlt: (image && image.alt) || product.title,
        createdAt: product.created_at ? Date.parse(product.created_at) : 0,
      };
    });
    return {
      products: products,
      hasNextPage: (json.products || []).length >= 250,
    };
  }

  function parseSearchPage(json) {
    var products = (json.products || []).map(function (product) {
      return {
        title: product.title,
        handle: product.handle,
        url: product.url,
        available: !!product.available,
        price: product.price != null ? parseFloat(product.price) : null,
        compareAtPrice: product.compare_at_price != null ? parseFloat(product.compare_at_price) : null,
        image: product.image || null,
        imageAlt: product.image_alt || product.title,
        // templates/search.brand-search.liquid emits created_at as a Unix
        // timestamp (seconds) via Liquid's `date: '%s'` filter - a raw
        // `{{ item.created_at | json }}` on a Shopify Time object serializes
        // as a decomposed array of its internal fields, not an ISO string,
        // which Date.parse() can't read (confirmed empirically). Convert
        // the epoch-seconds string to epoch-milliseconds instead.
        createdAt: product.created_at ? parseInt(product.created_at, 10) * 1000 : 0,
      };
    });
    return {
      products: products,
      hasNextPage: !!json.has_next_page,
    };
  }

  function formatMoney(amount) {
    try {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AED';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  function renderCard(product) {
    var li = document.createElement('li');
    li.className = 'grid__item';
    // Marks this <li> as ours so guardGridList() (below) can tell it apart
    // from any native product Dawn's own infinite-scroll might splice into
    // the same list later.
    li.setAttribute('data-brand-search-card', 'true');

    var badge = '';
    if (product.available === false) {
      badge =
        '<div class="card__badge badge-position"><span class="badge badge--bottom-left">' +
        (window.brandSearchStrings && window.brandSearchStrings.soldOut ? window.brandSearchStrings.soldOut : 'Sold out') +
        '</span></div>';
    }

    var priceHtml = '';
    if (product.price != null) {
      if (product.compareAtPrice && product.compareAtPrice > product.price) {
        priceHtml =
          '<div class="price price--on-sale"><div class="price__container">' +
          '<div class="price__sale"><s class="price-item price-item--regular">' +
          formatMoney(product.compareAtPrice) +
          '</s> <span class="price-item price-item--sale price-item--last">' +
          formatMoney(product.price) +
          '</span></div></div></div>';
      } else {
        priceHtml =
          '<div class="price"><div class="price__container"><div class="price__regular">' +
          '<span class="price-item price-item--regular">' +
          formatMoney(product.price) +
          '</span></div></div></div>';
      }
    }

    // NOTE: card__content must be a DIRECT CHILD of .card (a sibling of
    // .card__inner) - matching Dawn's real card markup exactly. This isn't
    // cosmetic: assets/component-card.css has several rules that only take
    // effect through that exact parent/child relationship, e.g.
    // `.card--standard > .card__content { padding: 0; }` and
    // `.card:not(.card--horizontal) > .card__content > .card__badge`. An
    // earlier version of this function closed .card too early and rendered
    // card__content as a sibling of .card instead of inside it, which is the
    // most likely cause of the "data isn't showing under the images" report.
    li.innerHTML =
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--standard card--media" style="--ratio-percent: 125%;">' +
      '<div class="card__inner ratio" style="--ratio-percent: 125%;">' +
      '<div class="card__media"><div class="media media--transparent">' +
      (product.image
        ? '<img src="' +
          product.image +
          '" alt="' +
          escapeHtml(product.imageAlt || '') +
          '" loading="lazy" class="motion-reduce">'
        : '') +
      '</div></div>' +
      '</div>' +
      '<div class="card__content"><div class="card__information">' +
      '<h3 class="card__heading h5"><a href="' +
      product.url +
      '" class="full-unstyled-link">' +
      escapeHtml(product.title) +
      '</a></h3>' +
      '<div class="card-information">' +
      priceHtml +
      '</div>' +
      '</div>' +
      badge +
      '</div>' +
      '</div>' +
      '</div>';

    return li;
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function getGridContainer() {
    return document.getElementById('ProductGridContainer');
  }

  // The actual product <ul> is what needs the CSS Grid classes
  // ("grid product-grid grid--N-col-..."). Which element carries
  // id="product-grid" differs between Dawn's two templates: on collection
  // pages it's the <ul> itself, but on the search results page it's an
  // outer wrapping <div> around both the <ul> AND the pagination/loading
  // overlay - querying by that id and appending cards into it (as this
  // file used to do) puts the cards outside the grid entirely, so they
  // stack as plain block elements instead of laying out in columns. Target
  // the <ul> by its class instead, which is present and correct in both
  // templates.
  function getProductGridList(gridContainer) {
    return gridContainer.querySelector('ul.product-grid');
  }

  function getInfiniteScrollEl() {
    return document.querySelector('infinite-scroll');
  }

  // Dawn's own <infinite-scroll> element (snippets/infinite-scroll.liquid,
  // not a file this feature controls) watches ITSELF with an
  // IntersectionObserver as soon as it's parsed, completely independent of
  // this script. On a short results page (few matches) that observer can
  // fire almost immediately - often before this deferred script even runs -
  // and its fetchNextPage() splices Shopify's raw next page of native,
  // relevance-ordered, unfiltered results straight into the results wrapper
  // via insertAdjacentHTML("beforeend", ...). That inserts a WHOLE SECOND
  // "ul.product-grid" as a sibling after the first one. Earlier versions of
  // this file only ever found/cleared the FIRST such <ul>, so that leftover
  // second list of unrelated/unsorted native products stayed on screen right
  // after our own cards - this is what produced the "only the first few
  // results are related, the rest aren't" report. cleanGridContainer() below
  // defends against that regardless of whether the race was already lost by
  // the time we run, and detachInfiniteScroll() stops it from happening
  // again for the rest of this page view.
  function cleanGridContainer(gridContainer) {
    var lists = gridContainer.querySelectorAll('ul.product-grid');
    var list = lists.length ? lists[0] : null;
    for (var i = 1; i < lists.length; i++) {
      lists[i].remove();
    }
    Array.prototype.forEach.call(gridContainer.querySelectorAll('.loading-overlay'), function (el) {
      el.remove();
    });
    Array.prototype.forEach.call(gridContainer.querySelectorAll('.pagination'), function (el) {
      el.remove();
    });
    return list;
  }

  function detachInfiniteScroll() {
    var infiniteScrollEl = getInfiniteScrollEl();
    if (infiniteScrollEl && infiniteScrollEl.parentNode) {
      infiniteScrollEl.parentNode.removeChild(infiniteScrollEl);
    }
  }

  // On the COLLECTION template, Dawn's infinite-scroll targets the
  // "ul#product-grid" ITSELF (see snippets/infinite-scroll.liquid's
  // initGrid(), which matches "#product-grid" - on collection pages that id
  // is on the <ul>, not a wrapping <div> like on the search template - see
  // getProductGridList()'s comment above). That means detachInfiniteScroll()
  // isn't always enough: if its IntersectionObserver had ALREADY fired and
  // started a fetch before this script got a chance to run/detach it (very
  // possible - it observes itself the instant the element is parsed, and a
  // short results page can already have it in view on load), that fetch is
  // already in flight and can't be cancelled. When it resolves - even well
  // after we've finished rendering our own filtered/sorted cards - it will
  // still splice Shopify's raw, unfiltered next page of native <li> products
  // straight into that same, live "ul#product-grid" node reference, which is
  // exactly what produced "only the first few results match the brand I
  // typed, the rest don't". Guard against that for as long as we're driving
  // this list: watch it and immediately remove any child that isn't one of
  // our own (marked) cards, however and whenever it gets added.
  var gridGuard = null;

  function guardGridList(list) {
    if (gridGuard && gridGuard.target === list) return;
    if (gridGuard) gridGuard.observer.disconnect();
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType === 1 && !node.hasAttribute('data-brand-search-card')) {
            node.remove();
          }
        });
      });
    });
    observer.observe(list, { childList: true });
    gridGuard = { observer: observer, target: list };
  }

  function stopGuardingGrid() {
    if (gridGuard) {
      gridGuard.observer.disconnect();
      gridGuard = null;
    }
  }

  function ensureStatusEl(gridContainer) {
    var status = gridContainer.querySelector('.brand-search-status');
    if (!status) {
      status = document.createElement('p');
      status.className = 'brand-search-status';
      status.setAttribute('role', 'status');
      status.hidden = true;
      gridContainer.insertBefore(status, gridContainer.firstChild);
    }
    return status;
  }

  var originalGridSnapshot = null;
  var infiniteScrollOriginal = null;

  function captureSnapshotIfNeeded() {
    if (originalGridSnapshot !== null) return;
    var gridContainer = getGridContainer();
    if (gridContainer) {
      originalGridSnapshot = gridContainer.innerHTML;
    }
    // <infinite-scroll> lives OUTSIDE #ProductGridContainer (it's rendered
    // as a sibling after the whole filters+grid wrapper - see
    // sections/main-search.liquid), so the gridContainer snapshot above
    // never includes it. Remember where it originally was so it can be put
    // back later if we end up detaching it.
    var infiniteScrollEl = getInfiniteScrollEl();
    if (infiniteScrollEl && infiniteScrollOriginal === null) {
      infiniteScrollOriginal = {
        el: infiniteScrollEl,
        parent: infiniteScrollEl.parentNode,
        nextSibling: infiniteScrollEl.nextSibling,
      };
    }
  }

  function restoreSnapshot() {
    stopGuardingGrid();
    var gridContainer = getGridContainer();
    if (gridContainer && originalGridSnapshot !== null) {
      gridContainer.innerHTML = originalGridSnapshot;
    }
    if (infiniteScrollOriginal && !document.contains(infiniteScrollOriginal.el) && infiniteScrollOriginal.parent) {
      if (
        infiniteScrollOriginal.nextSibling &&
        infiniteScrollOriginal.nextSibling.parentNode === infiniteScrollOriginal.parent
      ) {
        infiniteScrollOriginal.parent.insertBefore(infiniteScrollOriginal.el, infiniteScrollOriginal.nextSibling);
      } else {
        infiniteScrollOriginal.parent.appendChild(infiniteScrollOriginal.el);
      }
    }
  }

  function renderResults(matches, term) {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;

    captureSnapshotIfNeeded();

    var strings = getStrings();
    detachInfiniteScroll();

    var list = cleanGridContainer(gridContainer);
    if (!list) {
      // Empty-collection/empty-search state currently rendered - build a
      // grid to render into (matching this store's configured column
      // counts: 2 on mobile/tablet, 4 on desktop).
      gridContainer.innerHTML =
        '<div class="collection page-width"><ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list"></ul></div>';
      list = getProductGridList(gridContainer);
    }

    list.innerHTML = '';
    guardGridList(list);

    var status = ensureStatusEl(gridContainer);

    if (matches.length === 0) {
      status.hidden = false;
      status.textContent = formatString(strings.noResults, { terms: term });
      return;
    }

    status.hidden = false;
    status.textContent = formatString(strings.resultsCount, { count: matches.length });

    matches.slice(0, MAX_RENDERED_RESULTS).forEach(function (product) {
      list.appendChild(renderCard(product));
    });
  }

  function renderDateSortedResults(products) {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;

    captureSnapshotIfNeeded();

    var strings = getStrings();
    detachInfiniteScroll();

    var list = cleanGridContainer(gridContainer);
    if (!list) {
      gridContainer.innerHTML =
        '<div class="collection page-width"><ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list"></ul></div>';
      list = getProductGridList(gridContainer);
    }

    list.innerHTML = '';
    guardGridList(list);

    var status = ensureStatusEl(gridContainer);

    if (products.length === 0) {
      // Nothing to show that Shopify's own (relevance) results didn't already
      // determine - leave whatever was already rendered alone rather than
      // claiming "no results".
      status.hidden = true;
      return;
    }

    status.hidden = false;
    status.textContent = formatString(strings.dateSortedStatus, { count: products.length });

    products.slice(0, MAX_RENDERED_RESULTS).forEach(function (product) {
      list.appendChild(renderCard(product));
    });
  }

  // Shopify's own /search endpoint can widen a query beyond exact title
  // matches once the strict match count is small (its built-in search
  // broadens to related/recommended products) - our full-result-set fetch
  // reads that very same result set, so it can legitimately include some of
  // those looser matches too. Sorting strictly by relevance naturally pushes
  // them to the bottom where they're barely noticed, but sorting by date
  // scrambles that ordering completely, so they can surface anywhere in the
  // list. Re-apply a simple title-contains filter against the page's own
  // search terms before date-sorting, as a safety net, so "Date, new to
  // old" only ever reorders products that actually match the query.
  function filterToQueryTerms(products, terms) {
    var needle = (terms || '').trim().toLowerCase();
    if (!needle) return products;
    return products.filter(function (product) {
      return product.title && product.title.toLowerCase().indexOf(needle) !== -1;
    });
  }

  function runDefaultDateSort(container) {
    showLoading(container);
    var terms = container.dataset.brandSearchTerms || '';
    fetchAllProducts(container).then(function (products) {
      var relevant = filterToQueryTerms(products, terms);
      var sorted = relevant.slice().sort(function (a, b) {
        return (b.createdAt || 0) - (a.createdAt || 0);
      });
      renderDateSortedResults(sorted);
    });
  }

  function isDateSortSelect(el) {
    return !!el && el.tagName === 'SELECT' && /^SortBy/.test(el.id);
  }

  // The search results page injects an extra "Date, new to old" option into
  // the native Sort-by dropdowns (see snippets/facets.liquid) since Shopify
  // itself has no such sort for search. Selecting it (or landing on the page
  // with it selected by default) must NOT go through Dawn's normal
  // sort_by=... page/AJAX reload (that value isn't a real Shopify sort key),
  // so we intercept the event before Dawn's own facets.js handles it.
  function initDateSortOverride() {
    function handle(event) {
      var select = event.target;
      if (!isDateSortSelect(select)) return;
      var container = select.closest('.facets-container');
      if (!container || container.dataset.brandSearchScope !== 'search') return;
      if (select.value !== DATE_SORT_VALUE) return;

      event.stopPropagation();
      if (event.cancelable) event.preventDefault();

      var siblingSelects = container.querySelectorAll('select[id^="SortBy"]');
      Array.prototype.forEach.call(siblingSelects, function (other) {
        if (other !== select) other.value = DATE_SORT_VALUE;
      });

      runDefaultDateSort(container);
    }

    document.addEventListener('input', handle, true);
    document.addEventListener('change', handle, true);
  }

  function initDateSortDefault() {
    // Shopify's `search.sort_by` Liquid property is never actually blank -
    // it silently resolves to "relevance" even when the URL has no sort_by
    // param at all, so the theme can't reliably tell "no explicit choice"
    // from "explicitly relevance" server-side. Do that check here instead,
    // straight from the real URL, and force the dropdown/grid into
    // "Date, new to old" whenever no OTHER sort was explicitly requested.
    var params = new URLSearchParams(window.location.search);
    var explicitSort = params.get('sort_by');
    var shouldDefaultToDate = !explicitSort || explicitSort === DATE_SORT_VALUE;
    if (!shouldDefaultToDate) return;

    document.querySelectorAll('.facets-container[data-brand-search-scope="search"]').forEach(function (container) {
      var selects = container.querySelectorAll('select[id^="SortBy"]');
      if (!selects.length) return;
      Array.prototype.forEach.call(selects, function (select) {
        select.value = DATE_SORT_VALUE;
      });
      runDefaultDateSort(container);
    });

    if (!explicitSort) {
      try {
        params.set('sort_by', DATE_SORT_VALUE);
        var newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
        window.history.replaceState(window.history.state, '', newUrl);
      } catch (e) {
        // Not fatal - the visible sort/grid are already correct either way.
      }
    }
  }

  function showLoading(container) {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;
    captureSnapshotIfNeeded();
    var status = ensureStatusEl(gridContainer);
    status.hidden = false;
    status.textContent = getStrings().loading;
  }

  function runSearch(container, term) {
    if (!term) {
      restoreSnapshot();
      var gridContainer = getGridContainer();
      if (gridContainer) {
        var status = gridContainer.querySelector('.brand-search-status');
        if (status) status.hidden = true;
      }
      return;
    }

    showLoading(container);

    fetchAllProducts(container).then(function (products) {
      var needle = term.trim().toLowerCase();
      var matches = products.filter(function (product) {
        return product.title && product.title.toLowerCase().indexOf(needle) !== -1;
      });
      // Brand search results are always shown newest-first (date, new to old),
      // regardless of whatever sort order the underlying collection/search page
      // is currently using.
      matches.sort(function (a, b) {
        return (b.createdAt || 0) - (a.createdAt || 0);
      });
      renderResults(matches, term);
    });
  }

  function syncInputs(value, source) {
    document.querySelectorAll('[data-brand-search-input]').forEach(function (input) {
      if (input !== source) input.value = value;
    });
  }

  // Delegated from the document, bound EXACTLY ONCE (see initInputs.bound
  // below) instead of re-querying "[data-brand-search-input]" /
  // ".brand-search-toggle" and attaching a fresh addEventListener to each
  // one every time init() runs. init() re-runs often - on the initial page
  // load, on every "shopify:section:load", and via the MutationObserver
  // watching the filters sidebar - and Dawn's own facets.js can replace
  // parts of that sidebar (e.g. after a native filter/sort change) with
  // brand-new toggle/input/clear-button DOM nodes at any time. The old
  // per-element listeners either stayed bound to now-detached nodes (so
  // clicking the fresh button silently did nothing - "sometimes it doesn't
  // open") or, when the same node survived a re-run, got a SECOND listener
  // stacked on top of the first (so one click fired the toggle logic twice
  // in a row and visually nothing seemed to happen - "it freezes/hangs").
  // Event delegation sidesteps both: there is nothing to rebind, because the
  // listener is never attached to the element itself.
  //
  // The brand-search input/box lives inside Dawn's own native
  // <form id="FacetFiltersForm"> (see snippets/facets.liquid) alongside the
  // real filter/sort controls. Dawn's facet-filters-form custom element
  // (assets/facets.js) listens for "input"/"change" bubbling up from
  // ANYWHERE inside that form and immediately fires its own AJAX refresh of
  // the whole filters+grid area - that's how its native checkbox/price
  // filters apply without a submit button. Every keystroke here was
  // bubbling into that same listener, racing our own debounced search: Dawn
  // would replace the form's HTML with a freshly server-rendered (empty,
  // closed) copy of the brand-search box mid-type, which is what looked
  // like the box "closing itself" while typing and needing 2-3 tries before
  // results actually stuck. Stopping propagation at the capture phase for
  // every event coming from the brand-search input keeps Dawn's form
  // listener from ever seeing them, while this script's own delegated
  // listeners (registered the same way, on the document) still fire.
  //
  // On top of that, the flow itself is now DELIBERATELY submit-driven
  // instead of live-as-you-type: typing only updates the box's own value
  // and clear button, it no longer triggers a search by itself. The search
  // only runs when the customer presses Enter or clicks the new search
  // button (.brand-search-submit), exactly like a normal search box.
  function initInputs() {
    if (initInputs.bound) return;
    initInputs.bound = true;

    // Stop every relevant event from a brand-search control from ever
    // reaching Dawn's native FacetFiltersForm listener. Capture phase, so
    // this runs before the event has a chance to bubble into anything else.
    ['input', 'change', 'keydown', 'keyup', 'keypress'].forEach(function (type) {
      document.addEventListener(
        type,
        function (event) {
          var target = event.target;
          if (!target || !target.hasAttribute) return;
          if (!target.hasAttribute('data-brand-search-input')) return;
          event.stopPropagation();
        },
        true
      );
    });

    document.addEventListener('input', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-brand-search-input')) return;
      syncInputs(input.value, input);
      var wrapper = input.closest('.brand-search-box');
      var clearBtn = wrapper ? wrapper.querySelector('.brand-search-clear') : null;
      if (clearBtn) clearBtn.hidden = input.value.length === 0;
      // No auto-search here anymore - typing only updates the box itself.
      // The actual search runs on Enter or on clicking the search button
      // (see the keydown and click handlers below).
    });

    document.addEventListener('keydown', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-brand-search-input')) return;
      if (event.key !== 'Enter') return;
      // Prevent the native <form> from submitting/reloading the page - we
      // handle the search ourselves, in place.
      event.preventDefault();
      var container = input.closest('.facets-container');
      if (!container) return;
      runSearch(container, input.value.trim());
    });

    document.addEventListener('click', function (event) {
      var clearBtn = event.target.closest && event.target.closest('.brand-search-clear');
      if (clearBtn) {
        var wrapper = clearBtn.closest('.brand-search-box');
        var input = wrapper ? wrapper.querySelector('[data-brand-search-input]') : null;
        var container = input ? input.closest('.facets-container') : null;
        document.querySelectorAll('[data-brand-search-input]').forEach(function (i) {
          i.value = '';
        });
        clearBtn.hidden = true;
        if (container) runSearch(container, '');
        return;
      }

      var submitBtn = event.target.closest && event.target.closest('.brand-search-submit');
      if (submitBtn) {
        var submitWrapper = submitBtn.closest('.brand-search-box');
        var submitInput = submitWrapper ? submitWrapper.querySelector('[data-brand-search-input]') : null;
        var submitContainer = submitInput ? submitInput.closest('.facets-container') : null;
        if (submitContainer) runSearch(submitContainer, submitInput.value.trim());
        return;
      }

      var toggle = event.target.closest && event.target.closest('.brand-search-toggle');
      if (toggle) {
        var box = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!box) return;
        var isHidden = box.hidden;
        box.hidden = !isHidden;
        toggle.setAttribute('aria-expanded', String(isHidden));
        if (isHidden) {
          var innerInput = box.querySelector('input');
          if (innerInput) innerInput.focus();
        }
      }
    });
  }

  function init() {
    initInputs();
    initDateSortDefault();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Registered once, on the document, so it keeps intercepting the sort
  // dropdowns even after facets.js/Dawn re-creates them during an AJAX
  // facet refresh - no need to re-attach this per re-render.
  initDateSortOverride();

  // The facets AJAX refresh (assets/facets.js) swaps large chunks of markup
  // back in (e.g. after a filter or a pagination change), which re-creates
  // the brand search inputs/toggles. Re-bind whenever that happens so the
  // controls keep working, and forget any cached "original grid" snapshot
  // since the grid content underneath it has legitimately changed. The
  // freshly-rendered Sort-by dropdown may again default to "Date, new to
  // old" (see snippets/facets.liquid), so re-check that too.
  document.addEventListener('shopify:section:load', function () {
    originalGridSnapshot = null;
    infiniteScrollOriginal = null;
    stopGuardingGrid();
    init();
  });

  var facetsWrapperObserverTargets = ['#main-collection-filters', '#main-search-filters'];
  facetsWrapperObserverTargets.forEach(function (selector) {
    var target = document.querySelector(selector);
    if (!target) return;
    var observer = new MutationObserver(
      debounce(function () {
        originalGridSnapshot = null;
        infiniteScrollOriginal = null;
        stopGuardingGrid();
        init();
      }, 50)
    );
    observer.observe(target, { childList: true, subtree: false });
  });
})();
